/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, {
  useState,
  useRef,
  createContext,
  useContext,
  useCallback,
} from 'react';

import type { Swiper as SwiperInstance } from 'swiper';

interface CarouselV2ContextProviderProps {
  /** Children to render within the context */
  children: React.ReactNode;
  /** Initial active slide index */
  initialIndex?: number;
  /** Optional reference to an existing Swiper instance */
  swiperInstance?: SwiperInstance | null;
}

interface CarouselContextType {
  /** Current active slide index */
  activeIndex: number;
  /** Whether the carousel is at the beginning */
  isBeginning: boolean;
  /** Whether the carousel is at the end */
  isEnd: boolean;
  goNext: () => void;
  /** Navigate to the previous slide */
  goPrev: () => void;
  /** Navigate to a specific slide by index */
  goToSlide: (index: number) => void;
  handleSwiper: (swiper: SwiperInstance) => void;
}

const CarouselContextV2 = createContext<CarouselContextType | undefined>(
  undefined,
);

/**
 * Hook to access carousel context
 * Use this in nested components to control the carousel
 */
export const useCarouselContextV2 = (): CarouselContextType => {
  const context = useContext(CarouselContextV2);
  if (!context) {
    throw new Error(
      'useCarouselContextV2 must be used within a CarouselV2ContextProvider',
    );
  }
  return context;
};

export const CarouselV2ContextProvider = ({
  children,
  initialIndex = 0,
}: CarouselV2ContextProviderProps) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const swiperRef = useRef<SwiperInstance | null>(null);

  const handleSwiper = useCallback(
    (swiper: SwiperInstance) => {
      swiperRef.current = swiper;

      const update = () => {
        setActiveIndex(swiper.activeIndex);
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
      };

      // If initialIndex is not 0, explicitly slide to that index
      if (initialIndex > 0) {
        swiper.slideTo(initialIndex, 0, false);
      }

      // initial sync
      swiper.once('afterInit', update); // ensures swiper is fully ready
      swiper.once('slidesLengthChange', update);
      swiper.once('snapGridLengthChange', update);
      swiper.on('slideChange', update);
      swiper.on('transitionEnd', update); // required for mousewheel scroll
      swiper.on('resize', update);
      swiper.on('activeIndexChange', update); // fallback

      // force sync after a small delay
      setTimeout(() => {
        swiper.update(); // force layout re-measurement
        update(); // re-sync nav buttons
      }, 100);
    },
    [initialIndex],
  );

  const goNext = () => {
    swiperRef.current?.slideNext();
  };

  const goPrev = () => {
    swiperRef.current?.slidePrev();
  };

  const goToSlide = (index: number) => {
    swiperRef.current?.slideTo(index);
  };

  const contextValue = {
    activeIndex,
    isBeginning,
    isEnd,
    goNext,
    goPrev,
    goToSlide,
    handleSwiper,
  };

  return (
    <CarouselContextV2.Provider value={contextValue}>
      {children}
    </CarouselContextV2.Provider>
  );
};
